import { ReactNode, useLayoutEffect, useRef, useState } from "react";

interface Props {
  label: string;
  className?: string;
  textClassName?: string;
  leading?: ReactNode;
}

function isTruncated(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

function TruncateTooltip({ label, className = "", textClassName = "truncate", leading }: Props) {
  const textRef = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const update = () => setTruncated(isTruncated(el));
    update();

    if (typeof ResizeObserver === "undefined")
      return;

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [label]);

  function showTooltip() {
    if (!truncated || !textRef.current) return;
    const { left, top } = textRef.current.getBoundingClientRect();
    setCoords({ left, top: top - 4 });
    setVisible(true);
  }

  return (
    <>
      <div
        className={`min-w-0 ${className}`}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setVisible(false)}
      >
        {leading}
        <div ref={textRef} className={textClassName}>{label}</div>
      </div>
      {visible && truncated &&
        <span
          className="tooltip-content fixed z-50 max-w-[240px] whitespace-normal -translate-y-full"
          style={{ left: coords.left, top: coords.top }}
        >
          {label}
        </span>
      }
    </>
  );
}

export default TruncateTooltip;
